import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { IBase, IClient, ITown } from 'src/app/models';
import { Gender, UserRole, UserStatus, UserType } from 'src/app/models/enums';
import { StorageService, UsersService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import {
  IUsersFilters,
  USER_FILTER_KEYS,
  deserializeUsersFilters,
  hasAnyFilterKey,
  parseUsersFiltersFromStorage,
} from 'src/app/filters';

@Component({
    selector: 'app-user-filter',
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss',
    standalone: false
})
export class UserFilterComponent implements OnInit, OnChanges, OnDestroy {
  private queryParamsSub?: Subscription;
  private initialized = false;
  public clients: any[] = [];
  public selectedValuesClients: IBase[] = [];
  public towns: any[] = [];
  public allTowns: any[] = [];
  public allOperators: any[] = [];
  public selectedValuesTowns: IBase[] = [];
  public operators: any[] = [];
  public selectedValuesOperators: IBase[] = [];
  public userStatus: any[] = [];
  public userGender: any[] = [];

  public UserStatus = UserStatus;
  public images = images;
  public TooltipPosition = TooltipPosition;

  public filterSelected = 'all';
  public filters: any[] = [];
  public filtersAdded: any[] = [];
  public showClients: boolean = false;
  public adminRole: UserRole;
  public UserType = UserType;

  @Output() filter = new EventEmitter();
  @Output() closeFilter = new EventEmitter();

  @Input() componentOpen: string;
  @Input() isFilterOpen: boolean;
  @Input() userType: UserType = UserType.Regular;

  @ViewChild('select') select: ElementRef;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    public usersService: UsersService,
    public utilsService: UtilsService,
    public storageService: StorageService,
    private activatedRoute: ActivatedRoute,
  ) {
    (Object.keys(Gender) as Array<keyof typeof Gender>).map(
      (gender: keyof typeof Gender) => {
        this.userGender.push({ value: gender, checked: false });
      },
    );
    this.filters.push({
      key: 'all',
      value: this.literalService.get('all', true),
    });
    this.filters.push({
      key: 'firstName',
      value: this.literalService.get('users.data.firstName', true),
    });
    this.filters.push({
      key: 'lastName',
      value: this.literalService.get('users.data.lastName', true),
    });
    this.filters.push({
      key: 'email',
      value: this.literalService.get('users.data.email', true),
    });
    this.filters.push({
      key: 'phoneNumber',
      value: this.literalService.get('users.data.phoneNumber', true),
    });
    this.filters.push({
      key: 'dni',
      value: this.literalService.get('users.data.dni', true),
    });

    renderer.listen('window', 'click', (e: Event) => {
      const modal = document.getElementById('filterContainer')!;
      const componentOpen = document.getElementById(this.componentOpen)!;
      const close = utilsService.closeCustomModals(e, modal, componentOpen);
      if (close) {
        this.closeFilter.emit();
        this.initFilters();
        this.select.nativeElement.checked = false;
      }
    });
  }

  async ngOnInit() {
    this.adminRole = await this.storageService.getRole();
    (Object.keys(UserStatus) as Array<keyof typeof UserStatus>).map(
      (userStatus: keyof typeof UserStatus) => {
        if (this.adminRole == 'SUPERADMIN' || userStatus != UserStatus.DELETED) {
          this.userStatus.push({ value: userStatus, checked: false });
        }
      },
    );

    // Keep modal in sync if URL changes externally (back/forward, programmatic nav).
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => this.initFilters());
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
  }

  async ngOnChanges(): Promise<void> {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    this.filters = [];
    this.filters.push({
      key: 'all',
      value: this.literalService.get('all', true),
    });
    this.filters.push({
      key: 'firstName',
      value: this.literalService.get('users.data.firstName', true),
    });
    this.filters.push({
      key: 'lastName',
      value: this.literalService.get('users.data.lastName', true),
    });
    this.filters.push({
      key: 'email',
      value: this.literalService.get('users.data.email', true),
    });
    this.filters.push({
      key: 'phoneNumber',
      value: this.literalService.get('users.data.phoneNumber', true),
    });
    this.filters.push({
      key: 'dni',
      value: this.literalService.get('users.data.dni', true),
    });

    if (this.isFilterOpen && !this.initialized) {
      this.initialized = true;
      const { clients, towns } = await this.usersService.getPossibleClientTowns();
      clients.map((client: IClient) => {
        this.clients.push({
          id: client.id,
          code: client.code,
          name: client.name,
        });
      });
      towns.map((town: ITown) => {
        this.towns.push({ id: town.id, code: town.name, name: town.name });
      });
      this.clients.sort(this.utilsService.compare('name'));
      this.towns.sort(this.utilsService.compare('name'));
      this.allTowns = [...this.towns];
      await this.loadOperators(clients.map((client: IClient) => client.id!));
      this.allOperators = [...this.operators];
      this.showClients = clients.length > 1;
      this.initFilters();
    }
  }

  async onClientsChange() {
    const clientIds = this.selectedValuesClients.map((client: IBase) => client.id!);
    if (clientIds.length === 0) {
      this.towns = [...this.allTowns];
      this.operators = [...this.allOperators];
    } else {
      const towns = (await this.usersService.getTownsByClient(clientIds)) as ITown[];
      this.towns = towns
        .map((town: ITown) => ({ id: town.id, code: town.name, name: town.name }))
        .sort(this.utilsService.compare('name'));
      await this.loadOperators(clientIds);
    }
    this.selectedValuesTowns = this.selectedValuesTowns.filter((town: IBase) =>
      this.towns.some((t: any) => t.id === town.id),
    );
    this.selectedValuesOperators = this.selectedValuesOperators.filter((operator: IBase) =>
      this.operators.some((o: any) => o.id === operator.id),
    );
  }

  private async loadOperators(clientIds: number[]) {
    const operators = await this.usersService.getOperatorsByClient(clientIds);
    this.operators = (operators as IBase[] ?? [])
      .map((operator: IBase) => ({ id: operator.id, code: operator.name, name: operator.name }))
      .sort(this.utilsService.compare('name'));
  }

  async initFilters() {
    const filters = await this.resolveCurrentFilters();
    if (!filters) return;
    this.selectedValuesClients = this.clients.filter((client: any) => filters.selectedClients.includes(client.id));
    this.selectedValuesTowns = this.towns.filter((town: any) => filters.selectedTowns.includes(town.id));
    this.selectedValuesOperators = this.operators.filter(
      (operator: any) => filters.selectedOperators.includes(operator.id),
    );

    this.userStatus.map((status) => {
      status.checked = filters.selectedStatus.some((s: any) => s.toLowerCase() === status.value.toLowerCase());
    });
  }

  private async resolveCurrentFilters(): Promise<IUsersFilters | null> {
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (hasAnyFilterKey(params, USER_FILTER_KEYS)) {
      return deserializeUsersFilters(params);
    }
    return parseUsersFiltersFromStorage(await this.storageService.getUsersFilters());
  }

  addFilter(data: string) {
    data.trim() !== '' &&
      this.filtersAdded.push({ key: this.filterSelected, value: data });
  };

  changeFilter(event: any) {
    this.filterSelected = event.target.value;
  };

  getImage(val: string, imageFrom: string) {
    const indexImage =
      imageFrom === 'status'
        ? Object.keys(images.user).findIndex((key: string) => {
            return (
              key === 'user' + this.utilsService.capitalize(val.toLowerCase())
            );
          })
        : Object.keys(images.types).findIndex((key: string) => {
            return key === val.toLowerCase();
          });
    const image = Object.values(
      imageFrom === 'status' ? images.user : images.types,
    ).find((value, index) => {
      return index === indexImage ? value : null;
    });
    return image;
  };

  selectAll(event: any) {
    this.userStatus.map(
      (status: any) => (status.checked = event.currentTarget.checked),
    );
    if (event.currentTarget.checked) {
      this.clients.map((client: any) =>
        this.selectedValuesClients.push({ id: client.id, name: client.code }),
      );
      this.towns.map((town: any) =>
        this.selectedValuesTowns.push({ id: town.id, name: town.code }),
      );
      this.operators.map((operator: any) =>
        this.selectedValuesOperators.push({ id: operator.id, name: operator.code }),
      );
    } else {
      this.selectedValuesClients = [];
      this.selectedValuesTowns = [];
      this.selectedValuesOperators = [];
      this.filtersAdded = [];
    }
  };

  clearSelection() {
    this.userStatus.map((status: any) => (status.checked = false));
    this.selectedValuesClients = [];
    this.selectedValuesTowns = [];
    this.selectedValuesOperators = [];
    this.filtersAdded = [];
    this.select.nativeElement.checked = false;
  };

  async onSubmit() {
    const selectedClients: number[] = [];
    this.selectedValuesClients.forEach((client: IBase) => {
      selectedClients.push(client.id!);
    });

    // Towns only apply to regular users and operators only to drivers: the hidden one is never sent
    const selectedTowns: number[] = [];
    if (this.userType !== UserType.Driver) {
      this.selectedValuesTowns.forEach((town: IBase) => {
        town && selectedTowns.push(town.id!);
      });
    }
        
    const selectedOperators: number[] = [];
    if (this.userType === UserType.Driver) {
      this.selectedValuesOperators.forEach((operator: IBase) => {
        operator && selectedOperators.push(operator.id!);
      });
    }

    const selectedStatus: string[] = [];
    this.userStatus.forEach((status: any) => {
      status.checked && selectedStatus.push(this.utilsService.capitalize(status.value.toLowerCase()));
    });

    this.storageService.setUsersFilters(JSON.stringify({
      selectedClients,
      selectedTowns,
      selectedOperators,
      selectedStatus
    }))

    this.filter.emit({selectedClients, selectedTowns, selectedOperators, selectedStatus});
  };
}
